import { setCurrentTimezone } from '@/lib/dayjs'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type TimezoneContextValue = {
  timezone: string
  orgTimezone: string
  setTimezone: (tz: string | null | undefined) => void
  pushTimezone: (tz: string | null | undefined) => void
  popTimezone: () => void
}

const DEFAULT_TZ = 'America/Chicago'
const TimezoneContext = createContext<TimezoneContextValue | null>(null)

// This is an attempt to keep track of timezone changes as we navigate the app.
// At the highest level, we have the organization timezone (fallback to DEFAULT_TZ).
// However, once we are in a location specific page (any location settings, or perhap a
// sales order view page), we can be more specific and use the location timezone directly.
// We do that by pushing the location timezone onto a stack, and popping it off when we
// navigate away.
//
// Long-live manual memoization!
export function TimezoneProvider({
  organization,
  children,
}: {
  organization?: { timezone?: string | null } | null
  children?: ReactNode
}) {
  const baselineTz = organization?.timezone || DEFAULT_TZ

  const stackRef = useRef<string[]>([]) // LIFO stack of previous tz
  const currentTzRef = useRef(baselineTz)

  const [timezone, setTimezoneState] = useState(baselineTz)

  const setTimezone = useCallback((tz: string | null | undefined) => {
    const next = tz || DEFAULT_TZ
    currentTzRef.current = next
    setCurrentTimezone(next)
    setTimezoneState(next)
  }, [])

  const pushTimezone = useCallback(
    (tz: string | null | undefined) => {
      const next = tz || DEFAULT_TZ
      if (next === currentTzRef.current) return
      stackRef.current.push(currentTzRef.current)
      setTimezone(next)
    },
    [setTimezone]
  )

  const popTimezone = useCallback(() => {
    const prev = stackRef.current.pop()
    if (!prev) {
      // This can happen under normal circumstances and is not an error.
      // Just reset back to the baseline timezone.
      setTimezone(baselineTz)
      return
    }
    setTimezone(prev)
  }, [setTimezone, baselineTz])

  // Keep baseline in sync when org changes, but do not stomp active overrides.
  const syncOrgTimezone = useEffectEvent(() => {
    if (stackRef.current.length === 0) {
      setTimezone(baselineTz)
    }
  })

  useEffect(() => {
    syncOrgTimezone()
  }, [baselineTz])

  const value = useMemo(
    () => ({
      timezone,
      orgTimezone: baselineTz,
      setTimezone,
      pushTimezone,
      popTimezone,
    }),
    [timezone, baselineTz, setTimezone, pushTimezone, popTimezone]
  )

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
}

export function useTimezone() {
  const ctx = useContext(TimezoneContext)
  if (!ctx) throw new Error('useTimezone must be used within a TimezoneProvider')
  return ctx
}
